import React, { ReactNode, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FormattedChartDataPoint,
  LanguageNameSpaces,
  TimeRange,
} from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  selectConsolidatedPortfolioHistoryData,
  selectPortfolioHistoryGraphPrecision,
  selectUniqueAccountsPortfolioData,
} from "@shared/slices/portfolio.slice";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { formatTimeSeriesChartData } from "@shared/utils/charts";
import { portfolioFilters } from "@shared/slices/filters.slice";
import { sharedLib } from "@shared/services/shared.service";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import { DISPLAY_CURRENCIES } from "common/constants";
import {
  getAccountsTotals,
  withLivePoint,
} from "features/dashboard/common/utils";
import { BalancePoint } from "features/dashboard/instrument/BalanceChart.tsx";
import NewLookBalanceHistory from "features/portfolio/NewLook/NewLookBalanceHistory.tsx";

/** Fits the portfolio overview's chart zone (`PORTFOLIO_OVERVIEW_HEIGHT`). */
export const PORTFOLIO_CHANGE_CHART_HEIGHT = 140;

/** Formatted chart points as the balance chart's time / value pairs. */
export const toBalancePoints = (
  data: FormattedChartDataPoint[]
): BalancePoint[] =>
  data
    .filter((point) => point.rawX !== undefined)
    .map((point) => ({ time: Number(point.rawX), value: point.y }));

interface NewLookPortfolioChangeProps {
  /** Defaults to "Portfolio change". */
  title?: ReactNode;
  /** The page the helper tooltip reports from. */
  pageName?: string;
}

/**
 * The portfolio's history (the accounts page, and the assets page's
 * overview): the change over the selected range leads. The history's
 * snapshots are hourly, so it ends on the filtered accounts' live total,
 * the figure the allocation shows beside it.
 */
const NewLookPortfolioChange = ({
  title,
  pageName = "Accounts",
}: NewLookPortfolioChangeProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const dispatch = useDispatch();
  const timeRange = useSelector(
    portfolioFilters.selectors.selectTimeRangeFilter
  ) as TimeRange;
  const { isFetching } =
    sharedLib.sharedApi.portfolio.useGetAmountsHistoryQuery({
      time_range: timeRange,
    });
  const history = useSelector(selectConsolidatedPortfolioHistoryData);
  const precisionConfig = useSelector(selectPortfolioHistoryGraphPrecision);
  const accounts = useSelector(selectUniqueAccountsPortfolioData);
  const displayCurrency = useSelector(selectDisplayCurrency);
  const points = useMemo(() => {
    const live = accounts.length
      ? withLivePoint(
          history,
          getAccountsTotals(accounts, true).total,
          getAccountsTotals(accounts, false).total
        )
      : history;
    return toBalancePoints(
      formatTimeSeriesChartData(
        live,
        displayCurrency === DISPLAY_CURRENCIES.BTC ? 2 : 1,
        false,
        null,
        precisionConfig?.balance?.[displayCurrency] ?? 2
      )
    );
  }, [history, precisionConfig, accounts, displayCurrency]);

  return (
    <NewLookBalanceHistory
      data-testid="portfolio-change"
      points={points}
      timeRange={timeRange}
      onTimeRangeChange={(range) =>
        dispatch(portfolioFilters.actions.setTimeRangeFilter(range))
      }
      loading={isFetching}
      chartHeight={PORTFOLIO_CHANGE_CHART_HEIGHT}
      title={
        <>
          {title ?? t("overview.portfolio_change")}
          <HelperTooltip
            analyticsPageName={pageName}
            analyticsFieldName={t("common.portfolio_change")}
            description={t("common.portfolio_change_tooltip")}
          />
        </>
      }
    />
  );
};

export default React.memo(NewLookPortfolioChange);
